import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, Warning, XCircle } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ASSET_CLASSES = ['FOREX', 'CRYPTO', 'METALS', 'INDICES', 'ENERGY', 'SHARES', 'ETFS', 'FUTURES'];

const AdminInstruments = () => {
  const [instruments, setInstruments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [inst, st] = await Promise.all([
        axios.get(`${API}/admin/instruments`),
        axios.get(`${API}/market/data-status`).catch(() => ({ data: null })),
      ]);
      const sorted = [...inst.data].sort((a, b) => {
        const oa = a.display_order ?? 0, ob = b.display_order ?? 0;
        if (oa !== ob) return oa - ob;
        return (a.symbol || '').localeCompare(b.symbol || '');
      });
      setInstruments(sorted);
      setStatus(st.data);
    } catch (e) { toast.error('Failed to load instruments'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => instruments.filter(i => {
    if (filter !== 'ALL' && i.asset_class !== filter) return false;
    if (search && !`${i.symbol} ${i.name}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }), [instruments, filter, search]);

  const patch = async (inst, payload) => {
    setSavingId(inst.id);
    try {
      const r = await axios.put(`${API}/admin/instruments/${inst.id}`, payload);
      setInstruments(prev => prev.map(x => x.id === inst.id ? { ...x, ...r.data.instrument } : x));
      toast.success('Updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setSavingId(null); }
  };

  const StatusBanner = () => {
    if (!status) return null;
    if (!status.configured) {
      return (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4" data-testid="mds-banner-unconfigured">
          <Warning size={22} weight="fill" className="flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <strong>Market data provider not configured.</strong> No `TWELVE_DATA_API_KEY` or `MASSIVE_API_KEY` in backend .env. Clients currently see "Market data unavailable" instead of prices. Add a provider key to activate real-time quotes across all instruments.
          </div>
        </div>
      );
    }
    if (status.rate_limit?.paused) {
      const scope = status.rate_limit.last_429?.scope;
      const reset = status.rate_limit.day_paused_until || status.rate_limit.minute_paused_until;
      return (
        <div className="flex items-start gap-3 bg-orange-50 border border-orange-200 text-orange-900 rounded-lg p-4" data-testid="mds-banner-rate-limited">
          <XCircle size={22} weight="fill" className="flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <strong>Twelve Data {scope === 'day' ? 'daily quota exhausted' : 'per-minute limit hit'}.</strong> Clients will see "Market data unavailable" until quota resets at <code>{reset ? new Date(reset).toLocaleString() : 'the next window'}</code>. On the <strong>free tier (800 credits/day)</strong> this happens quickly — upgrade to Twelve Data <a href="https://twelvedata.com/pricing" target="_blank" rel="noopener noreferrer" className="underline font-semibold">Grow ($79/mo)</a> or <strong>Pro ($229/mo)</strong> for production usage.
          </div>
        </div>
      );
    }
    if (status.connected) {
      return (
        <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg p-4" data-testid="mds-banner-connected">
          <CheckCircle size={22} weight="fill" className="flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <strong>Market data connected</strong> via <code>{status.provider}</code>. Last quote {Math.round(status.seconds_since_last_quote || 0)}s ago.
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-start gap-3 bg-orange-50 border border-orange-200 text-orange-800 rounded-lg p-4" data-testid="mds-banner-stale">
        <XCircle size={22} weight="fill" className="flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <strong>Market data provider configured</strong> ({status.provider}) but no fresh quotes in the last minute
          {status.last_quote_at ? ` (last: ${new Date(status.last_quote_at).toLocaleString()})` : ''}.
          Check the ingest task / API key.
        </div>
      </div>
    );
  };

  return (
    <div className="p-8 space-y-4" data-testid="admin-instruments-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-brand-primary">Instruments</h1>
          <p className="text-slate-600 mt-1">{filtered.length} of {instruments.length} instruments</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="admin-instruments-refresh">Refresh</Button>
      </div>

      <StatusBanner />

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
        <strong>Note:</strong> Market Bid/Ask prices are read-only and come only from the market-data provider. Admin controls availability, ordering, and tradability — never the price itself.
      </div>

      <div className="bg-white p-4 rounded-xl border border-slate-200 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs">Search</Label>
          <Input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Symbol or name…" data-testid="filter-search" />
        </div>
        <div>
          <Label className="text-xs">Asset Class</Label>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="filter-class"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All</SelectItem>
              {ASSET_CLASSES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left uppercase text-xs tracking-wider text-slate-600">
              <th className="p-3">Symbol</th>
              <th className="p-3">Name</th>
              <th className="p-3">Asset Class</th>
              <th className="p-3">Category</th>
              <th className="p-3 text-center">Tradable</th>
              <th className="p-3 text-center">In Watchlist</th>
              <th className="p-3 text-right">Order</th>
              <th className="p-3 text-right">Spread</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="8" className="p-8 text-center"><div className="loader mx-auto"></div></td></tr>
              : filtered.length === 0 ? <tr><td colSpan="8" className="p-8 text-center text-slate-500">No matching instruments</td></tr>
              : filtered.map(inst => (
                <tr key={inst.id} className={`border-t border-slate-100 ${savingId === inst.id ? 'opacity-60' : ''}`} data-testid={`inst-row-${inst.symbol}`}>
                  <td className="p-3 font-semibold tabular-nums">{inst.symbol}</td>
                  <td className="p-3 text-slate-700">{inst.name}</td>
                  <td className="p-3">
                    <Select value={inst.asset_class} onValueChange={v => patch(inst, { asset_class: v })}>
                      <SelectTrigger className="h-8 min-w-[110px]" data-testid={`inst-class-${inst.symbol}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ASSET_CLASSES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-3">
                    <Input
                      defaultValue={inst.category || ''}
                      onBlur={(e) => e.target.value !== (inst.category || '') && patch(inst, { category: e.target.value })}
                      className="h-8 max-w-[110px]"
                      data-testid={`inst-category-${inst.symbol}`}
                    />
                  </td>
                  <td className="p-3 text-center">
                    <Switch
                      checked={inst.trading_enabled !== false}
                      onCheckedChange={(v) => patch(inst, { trading_enabled: v })}
                      data-testid={`inst-trading-${inst.symbol}`}
                    />
                  </td>
                  <td className="p-3 text-center">
                    <Switch
                      checked={inst.watchlist_visible !== false}
                      onCheckedChange={(v) => patch(inst, { watchlist_visible: v })}
                      data-testid={`inst-watchlist-${inst.symbol}`}
                    />
                  </td>
                  <td className="p-3 text-right">
                    <Input
                      type="number"
                      defaultValue={inst.display_order ?? 0}
                      onBlur={(e) => Number(e.target.value) !== (inst.display_order ?? 0) && patch(inst, { display_order: Number(e.target.value) })}
                      className="h-8 max-w-[80px] text-right"
                      data-testid={`inst-order-${inst.symbol}`}
                    />
                  </td>
                  <td className="p-3 text-right">
                    <Input
                      type="number"
                      step="0.00001"
                      defaultValue={inst.spread ?? 0}
                      onBlur={(e) => Number(e.target.value) !== (inst.spread ?? 0) && patch(inst, { spread: Number(e.target.value) })}
                      className="h-8 max-w-[100px] text-right"
                      data-testid={`inst-spread-${inst.symbol}`}
                    />
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminInstruments;
